import React from 'react';
import { Poppins, Tooltip } from 'src/common';
import { ComplianceStatus } from 'src/graphql/_generated';
import { useStateSelector } from 'src/redux';
import styled from 'styled-components';

const Div = styled.div<{ $color: string }>`
  .tooltip-children {
    display: inline-flex !important;
  }

  .contents {
    align-items: center;
    display: inline-flex !important;
    gap: 9px;
    border: 1px solid transparent;

    .indicator {
      width: 20px;
      height: 20px;
      border-radius: 10px;
      background-color: ${({ $color }) => $color};
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

interface ComplianceIndicatorProps {
  compliance: ComplianceStatus | null;
}

export const ComplianceIndicator: React.FC<ComplianceIndicatorProps> = ({ compliance }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);

  let title: string;
  let bg: string;
  let icon: React.ReactNode;
  let tooltipHtml: string = '';

  switch (compliance) {
    case ComplianceStatus.Compliant:
      title = 'Compliant';
      bg = colors.statusGreen;
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
          <path
            d="M4.21695 6.80025L1.55056 4.13386L0.642578 5.03544L4.21695 8.60982L11.89 0.936743L10.9884 0.0351562L4.21695 6.80025Z"
            fill={colors.white}
          />
        </svg>
      );
      break;
    case ComplianceStatus.NotCompliant:
      title = 'Not compliant';
      bg = colors.statusRed;
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
          <rect
            x="0.730769"
            y="0.730769"
            width="17.5385"
            height="17.5385"
            rx="8.76923"
            fill={colors.statusRed}
            stroke={colors.statusRed}
            strokeWidth="1.46154"
          />
          <path
            d="M14.3622 5.33136L13.3357 4.32422L9.26605 8.31708L5.19643 4.32422L4.16992 5.33136L8.23955 9.32422L4.16992 13.3171L5.19643 14.3242L9.26605 10.3314L13.3357 14.3242L14.3622 13.3171L10.2926 9.32422L14.3622 5.33136Z"
            fill="white"
          />
        </svg>
      );
      break;
    case ComplianceStatus.PartialCompliant:
      title = 'Partially compliant';
      bg = colors.brightBlue;
      icon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
          <rect
            x="0.730769"
            y="0.730769"
            width="17.5385"
            height="17.5385"
            rx="8.76923"
            fill={colors.brightBlue}
            stroke={colors.brightBlue}
            strokeWidth="1.46154"
          />
          <path
            d="M12.5438 5.52939H11.5282V5.11646C11.5282 4.8569 11.3156 4.64453 11.0558 4.64453C10.796 4.64453 10.5834 4.8569 10.5834 5.11646V5.52939H7.96174V5.11646C7.96174 4.8569 7.74917 4.64453 7.48937 4.64453C7.22956 4.64453 7.01699 4.8569 7.01699 5.11646V5.52939H6.00139C5.48178 5.52939 5.05664 5.95412 5.05664 6.47324V13.0566C5.05664 13.5757 5.48178 14.0004 6.00139 14.0004H12.532C13.0516 14.0004 13.4767 13.5757 13.4767 13.0566V6.47324C13.4885 5.95412 13.0634 5.52939 12.5438 5.52939ZM12.532 13.0448H6.00139V9.08062H12.532V13.0448Z"
            fill="white"
          />
        </svg>
      );
      tooltipHtml = `All linked controls should be<br>live to be compliant`;
      break;
    default:
      return null;
  }

  return (
    <Div $color={bg}>
      <Tooltip dataHtml={tooltipHtml} place="right">
        <div className="contents">
          <div className="indicator">{icon}</div>
          <Poppins px={14}>{title}</Poppins>
        </div>
      </Tooltip>
    </Div>
  );
};
